import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2 } from "lucide-react";

const BG =
  "https://images.unsplash.com/photo-1460551882935-745bdcaf8009?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHw0fHx2aW50YWdlJTIwbmV3JTIweW9yayUyMGNpdHklMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg3MTYwNzI2fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) navigate("/");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Left image panel */}
      <div className="relative hidden lg:block border-r border-border">
        <img src={BG} alt="Vintage New York architecture" className="absolute inset-0 h-full w-full object-cover grayscale" />
        <div className="absolute inset-0 bg-primary/40" />
        <div className="absolute bottom-0 left-0 p-10 text-primary-foreground">
          <div className="font-display text-5xl leading-none mb-3">My Geeks LLC</div>
          <p className="font-mono text-xs uppercase tracking-[0.3em]">Customer Relationship Management</p>
          <p className="mt-4 max-w-sm text-sm text-primary-foreground/80">
            An old-world ledger for a modern operation. Sales, technicians and administration — under one roof.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-8 lg:hidden">
            <Building2 className="h-6 w-6 text-primary" />
            <span className="font-display text-2xl text-primary">My Geeks LLC</span>
          </div>
          <h1 className="text-3xl mb-1">Sign in</h1>
          <p className="text-sm text-muted-foreground mb-8">Access your department workspace.</p>

          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                className="rounded-none"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@mygeeksllc.com"
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                className="rounded-none"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-sm text-destructive font-medium">
                {error}
              </p>
            )}
            <Button
              type="submit"
              data-testid="login-submit-button"
              disabled={loading}
              className="w-full rounded-none"
            >
              {loading ? "Signing in…" : "Enter Workspace"}
            </Button>
          </form>

          <div className="mt-8 border-t border-border pt-4 text-xs text-muted-foreground font-mono space-y-1">
            <div>admin@mygeeksllc.com · Admin@123</div>
            <div>sales@mygeeksllc.com · Sales@123</div>
            <div>tech@mygeeksllc.com · Tech@123</div>
          </div>
        </div>
      </div>
    </div>
  );
}
